import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import assets from "../../common/assets";
import { genericErrorConfig } from "../../common/exceptions";
import { unitsToListOptions } from "../../common/helpers/listOptions";
import { space } from "../../common/variables";
import Button from "../../components/Button";
import Container from "../../components/Container";
import Cell, { CellHeader, CellHeading } from "../../components/Grid/Cell";
import CellBlock, {
    CellBlockFooter,
    CellBlockSection,
    CellBlockValue,
} from "../../components/Grid/CellBlock";
import Grid from "../../components/Grid/Grid";
import { ModalContext } from "../../components/Modal";
import OptionsList from "../../components/OptionsList";
import Plate from "../../components/Plate";
import SubHeader from "../../components/SubHeader";
import Tag from "../../components/Tag";
import { useSelectedUnits } from "../../hooks/useSelectedUnits";
import { useCreateAdministratorMutation } from "../../mutations/useCreateAdministratorMutation";
import { useAllUnitsQuery } from "../../queries/units/useAllUnitsQuery";
import { applyHybrisUnitsAndFormErrors } from "../CustomerView/customerDetailsConfig";
import { useAdministratorInfoForm } from "./AdministratorDetailsView.hooks";
import AdministratorInfoForm from "./AdministratorInfoForm";
import {
    ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE,
    convertAdministratorForCreate,
    getModalAdministratorErrorText,
    mapHybrisAdministratorErrorField,
} from "./administratorDetailsConfig";
import { AdministratorInput } from "./administratorTypes";

const CreateAdministratorView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setModalOptions } = useContext(ModalContext);

    const [isEditingUnits, setIsEditingUnits] = useState(false);
    const {
        selectedUnits,
        setSelectedUnits,
        unitsError,
        setUnitsError,
        updateUnitList,
    } = useSelectedUnits();

    const form = useAdministratorInfoForm();
    const municipalityRole = form.watch("municipalityRole");
    const isOrdererAdmin = municipalityRole === "ORDERER_ADMIN";

    useEffect(() => {
        if (!isOrdererAdmin) {
            setSelectedUnits([]);
            setIsEditingUnits(false);
            setUnitsError(undefined);
        }
    }, [isOrdererAdmin, setSelectedUnits, setUnitsError]);

    const allUnitsQuery = useAllUnitsQuery();
    const allUnitsOptions = useMemo(
        () => unitsToListOptions(allUnitsQuery.data),
        [allUnitsQuery.data],
    );

    const createAdministratorMutation = useCreateAdministratorMutation({
        onSuccess: (createdAdministratorId) => {
            navigate(
                `${location.pathname.substr(
                    0,
                    location.pathname.lastIndexOf("/"),
                )}/${createdAdministratorId}`,
                { replace: true },
            );
        },
        onError: (error) => {
            setModalOptions(
                genericErrorConfig(
                    () => setModalOptions({ visible: false }),
                    "Kunde ej skapa administratör.",
                    getModalAdministratorErrorText(error),
                ),
            );

            applyHybrisUnitsAndFormErrors(
                error,
                mapHybrisAdministratorErrorField,
                (fieldName, fieldError) =>
                    form.setError(fieldName as any, fieldError),
                setUnitsError,
            );
        },
    });

    const createAdministrator = (input: AdministratorInput) => {
        createAdministratorMutation.mutate(input);
    };

    const cancelEditingUnits = () => {
        setSelectedUnits([]);
        setIsEditingUnits(false);
        setUnitsError(undefined);
    };

    const isMutationLoading = createAdministratorMutation.isPending;

    return (
        <Container>
            <SubHeader backArrow title={"Skapa ny administratör"} />
            <Plate padding="large" spaceBottom={true}>
                <Grid gutter={space.padding}>
                    <Cell gutter={space.padding} extraPaddingRight={space.xLarge}>
                        <CellHeading>Personuppgifter</CellHeading>
                        <CellBlock active={true} noOutlines={true}>
                            <AdministratorInfoForm
                                onCancel={() => navigate(-1)}
                                {...form}
                                isSubmitLoading={isMutationLoading}
                                isNewForm={true}
                                showRoleSelect
                                showCustomerCreationAllowed={isOrdererAdmin}
                                disableSubmit={isOrdererAdmin && selectedUnits.length === 0}
                                onSubmit={(data) => {
                                    if (
                                        data.municipalityRole === "ORDERER_ADMIN" &&
                                        selectedUnits.length === 0
                                    ) {
                                        setUnitsError(ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE);
                                        return;
                                    }

                                    createAdministrator(
                                        convertAdministratorForCreate(data, selectedUnits),
                                    );
                                }}
                            />
                        </CellBlock>
                    </Cell>
                    {isOrdererAdmin && (
                        <Cell gutter={space.padding} extraPaddingLeft={space.xLarge}>
                            <CellHeader>
                                <CellHeading>
                                    Enheter
                                    {selectedUnits?.length > 0 && ` (${selectedUnits.length})`}
                                </CellHeading>
                                {isEditingUnits && selectedUnits.length > 0 && (
                                    <div>
                                        <Tag>
                                            {selectedUnits.length === 1
                                                ? `1 vald enhet`
                                                : `${selectedUnits.length} valda enheter`}
                                        </Tag>
                                    </div>
                                )}
                            </CellHeader>
                            {selectedUnits?.length || isEditingUnits ? (
                                <CellBlock onEdit={cancelEditingUnits} active={isEditingUnits}>
                                    {isEditingUnits ? (
                                        <>
                                            <CellBlockSection>
                                                <CellBlockValue>
                                                    Välj vilka enheter som ska vara kopplade
                                                    till Beställare administratör.
                                                </CellBlockValue>
                                            </CellBlockSection>
                                            <CellBlockSection>
                                                {!!allUnitsOptions?.length && (
                                                    <OptionsList
                                                        overflowValue={space.medium}
                                                        setChoice={updateUnitList}
                                                        data={allUnitsOptions}
                                                        selectedData={selectedUnits}
                                                    />
                                                )}
                                            </CellBlockSection>
                                            {unitsError && (
                                                <CellBlockSection>
                                                    <CellBlockValue>
                                                        {unitsError}
                                                    </CellBlockValue>
                                                </CellBlockSection>
                                            )}
                                            <CellBlockFooter
                                                isLoading={isMutationLoading}
                                                onCancel={cancelEditingUnits}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {selectedUnits.map((unit) => (
                                                <CellBlockValue key={unit.id}>
                                                    {unit.name}
                                                </CellBlockValue>
                                            ))}
                                        </>
                                    )}
                                </CellBlock>
                            ) : (
                                <>
                                    <Button
                                        outsideTitle="Lägg till enheter"
                                        shape="round"
                                        onClick={() => setIsEditingUnits(true)}
                                        icon={assets.plus}
                                    />
                                    {unitsError && (
                                        <CellBlockValue>{unitsError}</CellBlockValue>
                                    )}
                                </>
                            )}
                        </Cell>
                    )}
                </Grid>
            </Plate>
        </Container>
    );
};

export default CreateAdministratorView;
